import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as MoreIcon } from "../../../assets/icons/3Dot.svg";
import { ReactComponent as BookmarkIcon } from "../../../assets/icons/bookmark.svg";
import { ReactComponent as EyeIcon } from "../../../assets/icons/eye.svg";
import { ReactComponent as HamburgerIcon } from "../../../assets/icons/hamburger.svg";
import { ReactComponent as SearchIcon } from "../../../assets/icons/search.svg";
import { ReactComponent as ShareIcon } from "../../../assets/icons/share.svg";
import useReaderStore from "../../../context/readerStore";
import useActionGuard from "../../../hooks/useActionGuard";
import {
  createBookmarkLabel,
  isPageBookmarked,
  toggleBookmark,
} from "../../../services/bookmarkService";
import { isFeatureAllowed } from "../../../services/drmPolicyService";
import { showComingSoonNotification } from "../../../services/notificationService";
import DisplayMenu from "../../Toolbar/DisplayMenu";
import MobileMenu from "../MobileMenu/MobileMenu";
import "./MobileTopBar.css";

const MobileTopBar = ({ onToggleHelp }) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDisplayMenuOpen, setIsDisplayMenuOpen] = useState(false);
  const displayMenuButtonRef = useRef(null);
  const {
    currentPage,
    documentType,
    toggleSearchSidebar,
    isSearchSidebarOpen,
    setSearchQuery: setGlobalSearchQuery,
    searchQuery: globalSearchQuery,
    annotations,
    setAnnotationData,
    setAnnotationLoading,
    drmPolicy,
    toggleTocSidebar,
    isTocSidebarOpen,
  } = useReaderStore();

  // Local search state for mobile topbar
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    if (globalSearchQuery !== searchQuery) {
      setSearchQuery(globalSearchQuery || "");
    }
  }, [globalSearchQuery, searchQuery]);

  // Get bookmarks from store
  const apiBookmarks = annotations?.bookmarks?.data || [];

  // Compute if current page is bookmarked
  const isCurrentBookmarked = useMemo(() => {
    if (!documentType || !currentPage) return false;
    return isPageBookmarked(apiBookmarks, currentPage);
  }, [apiBookmarks, currentPage, documentType]);

  const executeToggleBookmark = useCallback(async () => {
    if (!documentType || !currentPage) return;

    setAnnotationLoading("bookmarks", true);

    await toggleBookmark({
      pageNumber: currentPage,
      title: createBookmarkLabel(documentType, currentPage),
      currentBookmarks: apiBookmarks,
      onSuccess: (updatedBookmarks) => {
        setAnnotationData("bookmarks", updatedBookmarks);
        setAnnotationLoading("bookmarks", false);
      },
      onError: (error) => {
        console.error("Error toggling bookmark:", error);
        setAnnotationLoading("bookmarks", false);
      },
    });
  }, [
    documentType,
    currentPage,
    apiBookmarks,
    setAnnotationData,
    setAnnotationLoading,
  ]);

  const { run: handleToggleBookmark } = useActionGuard(executeToggleBookmark);

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    // Keep local input state for controlled input
    setSearchQuery(value);
    // Drive global search query so SearchSidebar (mobile) can run the debounced search
    setGlobalSearchQuery(value);
  };

  const handleCancelSearch = () => {
    // Clear local input and close sidebar; global search state (results, query, flags)
    // will be cleared by toggleSearchSidebar via readerStore.
    setSearchQuery("");
    toggleSearchSidebar();
  };

  const toggleDisplayMenu = useCallback(() => {
    setIsMenuOpen(false);
    setIsDisplayMenuOpen((open) => !open);
  }, []);

  const closeDisplayMenu = useCallback(() => {
    setIsDisplayMenuOpen(false);
  }, []);

  // Don't leave the eye menu "open" under the search chrome — when search
  // closes it would otherwise pop back unexpectedly.
  useEffect(() => {
    if (isSearchSidebarOpen) setIsDisplayMenuOpen(false);
  }, [isSearchSidebarOpen]);

  // Show only search input and cancel button when search sidebar is open

  if (isSearchSidebarOpen) {
    return (
      <div className="mobile-top-bar">
        <form
          className="mobile-top-actions mobile-top-actions--search"
          style={{ width: "100%" }}
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            type="search"
            className="mobile-search-input"
            placeholder={t("mobile.searchPlaceholder")}
            autoFocus
            style={{
              flex: 1,
              fontSize: 16,
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid var(--border-color)",
              marginRight: 8,
            }}
            value={searchQuery}
            onChange={handleSearchInputChange}
            enterKeyHint="search"
          />
          <button
            className="mobile-icon-button"
            aria-label={t("mobile.cancelSearch")}
            title={t("common.cancel")}
            type="button"
            onClick={handleCancelSearch}
          >
            {t("common.cancel")}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="mobile-top-bar">
      <div className="mobile-top-actions mobile-top-actions--leading">
        <button
          className={`mobile-icon-button${isTocSidebarOpen ? " is-active" : ""}`}
          onClick={toggleTocSidebar}
          aria-label={t("common.tableOfContents")}
          aria-pressed={isTocSidebarOpen}
          aria-expanded={isTocSidebarOpen}
          title={t("common.tableOfContents")}
          type="button"
        >
          <HamburgerIcon width={20} height={20} aria-hidden="true" />
        </button>
      </div>

      <div className="mobile-top-actions mobile-top-actions--trailing">
        {isFeatureAllowed(drmPolicy, "bookmarks") && (
          <button
            className={`mobile-icon-button bookmark-toggle-button${
              isCurrentBookmarked ? " is-active" : ""
            }`}
            onClick={handleToggleBookmark}
            aria-label={
              isCurrentBookmarked
                ? t("toolbar.removeBookmark")
                : t("toolbar.addBookmark")
            }
            aria-pressed={isCurrentBookmarked}
            title={
              isCurrentBookmarked
                ? t("toolbar.bookmarked")
                : t("toolbar.addBookmark")
            }
          >
            <BookmarkIcon width={20} height={20} aria-hidden="true" />
          </button>
        )}

        {isFeatureAllowed(drmPolicy, "search") && (
          <button
            className={`mobile-icon-button${
              isSearchSidebarOpen ? " is-active" : ""
            }`}
            onClick={toggleSearchSidebar}
            aria-label={t("common.search")}
            aria-pressed={isSearchSidebarOpen}
            title={t("common.search")}
          >
            <SearchIcon width={20} height={20} aria-hidden="true" />
          </button>
        )}

        <div className="mobile-display-menu">
          <button
            ref={displayMenuButtonRef}
            className={`mobile-icon-button${
              isDisplayMenuOpen ? " is-active" : ""
            }`}
            type="button"
            title={t("toolbar.view")}
            aria-label={t("toolbar.view")}
            aria-haspopup="menu"
            aria-expanded={isDisplayMenuOpen}
            onClick={toggleDisplayMenu}
          >
            <EyeIcon
              className="mobile-icon-eye"
              width={34}
              height={34}
              aria-hidden="true"
            />
          </button>
          <DisplayMenu
            isOpen={isDisplayMenuOpen}
            onClose={closeDisplayMenu}
            anchorRef={displayMenuButtonRef}
          />
        </div>

        <button
          className="mobile-icon-button is-coming-soon"
          type="button"
          title={t("toolbar.share")}
          aria-label={t("toolbar.share")}
          onClick={() => showComingSoonNotification("share")}
        >
          <ShareIcon
            className="mobile-icon-share"
            width={34}
            height={34}
            aria-hidden="true"
          />
        </button>

        <button
          className={`mobile-icon-button${isMenuOpen ? " is-active" : ""}`}
          onClick={() => {
            setIsDisplayMenuOpen(false);
            setIsMenuOpen((open) => !open);
          }}
          aria-label={t("common.menu")}
          aria-haspopup="true"
          aria-expanded={isMenuOpen}
          title={t("common.menu")}
        >
          <MoreIcon width={20} height={20} aria-hidden="true" />
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <MobileMenu
          onClose={() => setIsMenuOpen(false)}
          onToggleHelp={onToggleHelp}
        />
      )}
    </div>
  );
};

export default MobileTopBar;
